/**
 * opponent-signals — the Sprint 3 keystone (opponent scouting).
 *
 * Every game import already stores the OPPOSING team's at-bats (batterTeam='opp'
 * for their hitters, pitcherTeam='opp' for their pitchers) with full play-by-play
 * and an `opponentKey`. This module aggregates those across EVERY meeting with a
 * given opponent into a scouting report: their danger bats / soft spots / running
 * game by lineup slot, and their pitchers' command profile + fatigue cliff.
 *
 * Pure + parser-independent (operates on AtBatDoc[]). The output is consumed by
 * the Prep Sheet and narrated in the suggestive bench-coach voice — small-sample
 * honest ("small sample, but last time…"). Mirrors the math in game-signals.ts so
 * an opponent's FPS%/fatigue means the same thing as ours.
 */

import { AtBatDoc } from '../../types/dugout';

const HIT = new Set(['1B', '2B', '3B', 'HR']);
const XBH = new Set(['2B', '3B', 'HR']);
const NON_AB = new Set(['BB', 'HBP', 'SF', 'SAC']);

const pct = (n: number, d: number) => (d > 0 ? n / d : 0);
const isStrike = (p: string) => p === 'S' || p.startsWith('S');

export interface OppBatterScout {
  number: string;
  name: string;
  slotsSeen: number[];     // batting-order slots we've seen them hit in
  topSlot: number | null;  // most common slot (their usual spot)
  pa: number;
  reached: number;
  onBasePct: number;
  hits: number;
  xbh: number;
  strikeouts: number;
  soPct: number;
  walks: number;
  sb: number;
  danger: boolean;         // real extra-base pop or high on-base
  softSpot: boolean;       // we get them out (high K, low on-base) on a fair sample
}

export interface OppPitcherScout {
  number: string;
  name: string;
  battersFaced: number;
  pitchCount: number;
  strikePct: number;
  firstPitchStrikePct: number;
  walks: number;
  walkRate: number;
  strikeouts: number;
  hitsAllowed: number;
  /** Strike% early vs late across the sample — their fatigue tendency. */
  fatigue: { earlyStrikePct: number; lateStrikePct: number; dropPct: number; detected: boolean };
  /** Gets ahead but walks people: high FPS + high walk rate. */
  commandCollapse: boolean;
}

export interface OpponentScoutReport {
  opponentKey: string;
  games: number;
  batters: OppBatterScout[];   // sorted: danger first, then by on-base
  pitchers: OppPitcherScout[]; // sorted by batters faced (most-used first)
  thinSample: boolean;         // < 2 games — treat softly
}

const MIN_PA_BATTER = 4;   // below this, no danger/soft-spot call
const MIN_BF_PITCHER = 6;  // below this, no fatigue/command call

function mode(nums: number[]): number | null {
  if (!nums.length) return null;
  const counts = new Map<number, number>();
  for (const n of nums) counts.set(n, (counts.get(n) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0][0];
}

function key(ab: AtBatDoc, role: 'batter' | 'pitcher'): string {
  const num = role === 'batter' ? ab.batterNumber : ab.pitcherNumber;
  const name = role === 'batter' ? ab.batterName : ab.pitcherName;
  return (num && String(num).trim()) || (name || 'unknown').trim().toLowerCase();
}

function scoutBatters(oppBats: AtBatDoc[]): OppBatterScout[] {
  const groups = new Map<string, AtBatDoc[]>();
  for (const ab of oppBats) {
    const k = key(ab, 'batter');
    (groups.get(k) ?? groups.set(k, []).get(k)!).push(ab);
  }

  const out: OppBatterScout[] = [];
  for (const abs of groups.values()) {
    const pa = abs.length;
    const reached = abs.filter(a => a.reachedBase).length;
    const hits = abs.filter(a => HIT.has(a.resultCode)).length;
    const xbh = abs.filter(a => XBH.has(a.resultCode)).length;
    const so = abs.filter(a => a.resultCode === 'K').length;
    const bb = abs.filter(a => a.resultCode === 'BB').length;
    const sb = abs.filter(a => /\bSB\b/.test(a.result)).length;
    const slots = abs.map(a => a.lineupSlot).filter(s => s > 0);
    const onBasePct = pct(reached, pa);
    const soPct = pct(so, pa);
    out.push({
      number: String(abs[0].batterNumber || ''),
      name: abs[0].batterName,
      slotsSeen: [...new Set(slots)].sort((a, b) => a - b),
      topSlot: mode(slots),
      pa, reached, onBasePct, hits, xbh, strikeouts: so, soPct, walks: bb, sb,
      danger: pa >= MIN_PA_BATTER && (xbh >= 2 || onBasePct >= 0.5),
      softSpot: pa >= MIN_PA_BATTER && soPct >= 0.4 && onBasePct <= 0.25,
    });
  }
  // Danger bats first, then by on-base.
  return out.sort((a, b) => Number(b.danger) - Number(a.danger) || b.onBasePct - a.onBasePct);
}

function fatigueOf(ordered: AtBatDoc[]): OppPitcherScout['fatigue'] {
  const pitches: number[] = [];
  for (const ab of ordered) for (const p of ab.pitchSequence) pitches.push(isStrike(p) ? 1 : 0);
  const total = pitches.length;
  if (total < 25) return { earlyStrikePct: 0, lateStrikePct: 0, dropPct: 0, detected: false };
  const mid = Math.floor(total / 2);
  const early = pitches.slice(0, mid);
  const late = pitches.slice(mid);
  const earlyStrikePct = pct(early.reduce((a, b) => a + b, 0), early.length);
  const lateStrikePct = pct(late.reduce((a, b) => a + b, 0), late.length);
  const dropPct = earlyStrikePct - lateStrikePct;
  return { earlyStrikePct, lateStrikePct, dropPct, detected: dropPct >= 0.12 };
}

function scoutPitchers(oppPitches: AtBatDoc[]): OppPitcherScout[] {
  const groups = new Map<string, AtBatDoc[]>();
  for (const ab of oppPitches) {
    const k = key(ab, 'pitcher');
    (groups.get(k) ?? groups.set(k, []).get(k)!).push(ab);
  }

  const out: OppPitcherScout[] = [];
  for (const abs of groups.values()) {
    const ordered = [...abs].sort((a, b) => a.inning - b.inning);
    const bf = ordered.length;
    const pitchCount = ordered.reduce((s, a) => s + (a.pitchCount || a.pitchSequence.length), 0);
    const strikes = ordered.reduce((s, a) => s + (a.strikes ?? 0), 0);
    const walks = ordered.filter(a => a.resultCode === 'BB').length;
    const hbp = ordered.filter(a => a.resultCode === 'HBP').length;
    const so = ordered.filter(a => a.resultCode === 'K').length;
    const hits = ordered.filter(a => HIT.has(a.resultCode)).length;
    const withPitches = ordered.filter(a => a.pitchSequence.length > 0);
    const fps = pct(withPitches.filter(a => isStrike(a.pitchSequence[0])).length, withPitches.length);
    const walkRate = pct(walks + hbp, bf);
    out.push({
      number: String(ordered[0].pitcherNumber || ''),
      name: ordered[0].pitcherName || 'Unknown',
      battersFaced: bf,
      pitchCount,
      strikePct: pct(strikes, pitchCount),
      firstPitchStrikePct: fps,
      walks,
      walkRate,
      strikeouts: so,
      hitsAllowed: hits,
      fatigue: fatigueOf(ordered),
      commandCollapse: bf >= MIN_BF_PITCHER && fps >= 0.55 && walkRate >= 0.18,
    });
  }
  return out.sort((a, b) => b.battersFaced - a.battersFaced);
}

/**
 * Build a scouting report for one opponent from ALL at-bats we've stored against
 * them (across every meeting). Pass the at-bats already filtered to one
 * opponentKey, OR pass everything and set `opponentKey` to filter here.
 */
export function buildOpponentScout(atBats: AtBatDoc[], opponentKey: string): OpponentScoutReport {
  const mine = atBats.filter(ab => ab.opponentKey === opponentKey && ab.confidence !== 'low');
  const oppBats = mine.filter(ab => ab.batterTeam === 'opp');
  const oppPitches = mine.filter(ab => ab.pitcherTeam === 'opp');
  const games = new Set(mine.map(ab => ab.gameId)).size;
  return {
    opponentKey,
    games,
    batters: scoutBatters(oppBats),
    pitchers: scoutPitchers(oppPitches),
    thinSample: games < 2,
  };
}

export { MIN_PA_BATTER, MIN_BF_PITCHER };
